import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from "moment/moment";
import * as Yup from 'yup';

import { Box, Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';

import useNotification from '../../../../../core/hooks/use_notification';
import { getFoodPartnerState } from '../../../../../features/food_partner/get_food_partner';
import TextFieldFormik from '../../../../common/text_field';
import RadioGroupFormik from '../../../../common/radio_group';
import { SubmitButton } from '../../../../common/button';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { GET_ROUTE_FOOD_PARTNER_FORMS, MISC_DATE_ISO_TIME_ONLY_FORMAT } from '../../../../../core/constants';
import { createFoodPartnerAction, createFoodPartnerState } from '../../../../../features/food_partner/create_food_partner';
import { updateFoodPartnerAction, updateFoodPartnerState } from '../../../../../features/food_partner/update_food_partner';
import { createFoodPartnerNotificationData, updateFoodPartnerNotificationData } from '../../../../../core/utils/notification_utils';
import { yesNoOptions } from '../../../../common/form_utils';
import TimePickerFormik from "../../../../common/time_picker";
import { parseDateString } from "../../../../../core/utils/date_utils";
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';

const foodDeliveryTypeOptions = [
    { value: 'DELIVERY_TO_WAREHOUSE', label: 'Dostawa do magazynu głównego' },
    { value: 'SELF_PICKUP', label: 'Odbiór osobisty' },
];

const FoodPartnerForm = ({ foodPartnerId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const createForm = !foodPartnerId;

    const getFoodPartner = useSelector(getFoodPartnerState);
    const createFoodPartner = useSelector(createFoodPartnerState);
    const updateFoodPartner = useSelector(updateFoodPartnerState);

    const initialValues = {
        name: (!createForm && getFoodPartner?.data?.name) || '',
        shortName: (!createForm && getFoodPartner?.data?.shortName) || '',
        street: (!createForm && getFoodPartner?.data?.street) || '',
        zipCode: (!createForm && getFoodPartner?.data?.zipCode) || '',
        city: (!createForm && getFoodPartner?.data?.city) || '',
        voivodeship: (!createForm && getFoodPartner?.data?.voivodeship) || '',
        foodDeliveryType: (!createForm && getFoodPartner?.data?.foodDeliveryType) || foodDeliveryTypeOptions[0].value,
        deliveryNote: (!createForm && getFoodPartner?.data?.deliveryNote) || '',
        orderNote: (!createForm && getFoodPartner?.data?.orderNote) || '',
        nip: (!createForm && getFoodPartner?.data?.nip) || '',
        regon: (!createForm && getFoodPartner?.data?.regon) || '',
        krs: (!createForm && getFoodPartner?.data?.krs) || '',
        reservationEnabled: (!createForm && getFoodPartner?.data?.reservationEnabled) || false,
        reservationMaxHour: (!createForm && getFoodPartner?.data?.reservationMaxHour)
            ? moment(getFoodPartner?.data?.reservationMaxHour, MISC_DATE_ISO_TIME_ONLY_FORMAT)
            : '',
        supportWeekendOrdering: (!createForm && getFoodPartner?.data?.supportWeekendOrdering) || false,
        generateReservationDocumentsAutomatically: (!createForm && getFoodPartner?.data?.generateReservationDocumentsAutomatically) || false,
        tryUseExistingResources: (!createForm && getFoodPartner?.data?.tryUseExistingResources) || false,
        generalNote: (!createForm && getFoodPartner?.data?.generalNote) || ''
    };

    const schema = Yup.object().shape({
        name: Yup.string().required(),
        shortName: Yup.string(),
        street: Yup.string().required(),
        zipCode: Yup.string().required(),
        city: Yup.string().required(),
        voivodeship: Yup.string().required(),
        foodDeliveryType: Yup.string().required(),
        deliveryNote: Yup.string(),
        orderNote: Yup.string(),
        nip: Yup.string().required(),
        regon: Yup.string().required(),
        krs: Yup.string(),
        reservationEnabled: Yup.boolean().required(),
        reservationMaxHour: Yup.date()
            .transform(parseDateString)
            .nullable(),
        supportWeekendOrdering: Yup.boolean().required(),
        generateReservationDocumentsAutomatically: Yup.boolean().required(),
        tryUseExistingResources: Yup.boolean().required(),
        generalNote: Yup.string()
    });

    const onSubmit = (values) => {
        const reservationMaxHour = values?.reservationMaxHour?.format(MISC_DATE_ISO_TIME_ONLY_FORMAT);

        const form = {
            ...values,
            reservationMaxHour
        };

        if (createForm) {
            dispatch(createFoodPartnerAction({ form }))
                .then(response => {
                    showNotification(createFoodPartnerNotificationData(response));
                    response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(GET_ROUTE_FOOD_PARTNER_FORMS(response?.payload.id));
                });
        } else {
            dispatch(updateFoodPartnerAction({ form, id: foodPartnerId }))
                .then(response => {
                    showNotification(updateFoodPartnerNotificationData(response));
                });
        }
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }>
            { () => (
                <Box component={ Form }
                     sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>
                    <Box sx={ { flexGrow: 1, mb: 4 } }>
                        <Stack spacing={ 3 }>

                            <SectionPaper title="Dane Podstawowe i Rejestrowe">
                                <Grid container spacing={ 2 }>
                                    <Grid item xs={ 12 } md={ 8 }>
                                        <TextFieldFormik name="name" label="Pełna nazwa firmy" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik name="shortName" label="Nazwa skrócona"/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik name="nip" label="NIP" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik name="regon" label="REGON" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik name="krs" label="KRS"/>
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Adres Siedziby">
                                <Grid container spacing={ 2 }>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="street" label="Ulica i numer" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 2 }>
                                        <TextFieldFormik name="zipCode" label="Kod pocztowy" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 4 }>
                                        <TextFieldFormik name="city" label="Miasto" required/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="voivodeship" label="Województwo" required/>
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Zasady Dostaw i Zamówień">
                                <Grid container spacing={ 2 }>
                                    <Grid item xs={ 12 }>
                                        <RadioGroupFormik
                                            name="foodDeliveryType"
                                            label="Forma dostawy"
                                            options={ foodDeliveryTypeOptions }
                                            booleanOptions={ false }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="deliveryNote" label="Dni i godziny dostaw / odbiorów"
                                                         multiline rows={ 2 }/>
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TextFieldFormik name="orderNote"
                                                         label="Ile dni wcześniej i do której godziny trzeba składać zamówienia"
                                                         multiline rows={ 2 }/>
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Konfiguracja Rezerwacji i Aplikacji">
                                <Grid container spacing={ 2 }>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <RadioGroupFormik
                                            name="reservationEnabled"
                                            label="Czy użytkownicy mogą składać zamówienia?"
                                            options={ yesNoOptions }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <TimePickerFormik
                                            name="reservationMaxHour"
                                            label="Maksymalna godzina składania zamówień"
                                            InputLabelProps={ { shrink: true } }
                                            views={ ['hours'] }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <RadioGroupFormik
                                            name="supportWeekendOrdering"
                                            label="Wspiera składanie zamówień w weekend?"
                                            options={ yesNoOptions }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <RadioGroupFormik
                                            name="generateReservationDocumentsAutomatically"
                                            label="Generuj dokumenty rezerwacji automatycznie?"
                                            options={ yesNoOptions }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } md={ 6 }>
                                        <RadioGroupFormik
                                            name="tryUseExistingResources"
                                            label="Podczas generowania dokumentów używaj istniejących zasobów?"
                                            options={ yesNoOptions }
                                        />
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                            <SectionPaper title="Dodatkowe Informacje">
                                <Grid container spacing={ 2 }>
                                    <Grid item xs={ 12 }>
                                        <TextFieldFormik name="generalNote" label="Notatka ogólna" multiline
                                                         rows={ 3 }/>
                                    </Grid>
                                </Grid>
                            </SectionPaper>

                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <SubmitButton
                            isLoading={ createForm ? createFoodPartner?.loading : updateFoodPartner?.loading }>
                            Zapisz
                        </SubmitButton>
                    </BottomActionBar>

                </Box>
            ) }
        </Formik>
    );
};

export default FoodPartnerForm;